import { Button, TextField, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePageScrollLock } from "../hooks/usePageScrollLock";
import { financeApi } from "../lib/api";
import { userAvatarColor, userInitials } from "../lib/avatar";
import { useDebugEnabled } from "../lib/debugSetting";
import type { Group, User } from "../lib/types";

export const settingsPaths = new Set([
  "/settings",
  "/settings/users-family",
  "/settings/bank-accounts",
  "/settings/currencies",
  "/settings/categories",
  "/settings/tags",
  "/settings/linked-transactions",
  "/settings/mcp",
  "/settings/feedback",
  "/settings/debug",
]);

export function Sidebar({
  user,
  group,
  groups,
  selectedGroupId,
  onSelectGroup,
}: {
  user: User;
  group: Group;
  groups: Group[];
  selectedGroupId: string;
  onSelectGroup: (groupId: string) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const debugEnabled = useDebugEnabled();
  const [settingsOpen, setSettingsOpen] = useState(settingsPaths.has(location.pathname));
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 980px)");
  const isSettingsPage = settingsPaths.has(location.pathname);
  const effectiveSettingsOpen = settingsOpen || isSettingsPage;
  const shouldRenderMenuPanel = !isMobile || mobileOpen;
  usePageScrollLock(isMobile && mobileOpen);

  useEffect(() => {
    setMobileOpen(false);
    if (!settingsPaths.has(location.pathname)) {
      setSettingsOpen(false);
    }
  }, [location.pathname]);

  const goTo = (path: string) => {
    setMobileOpen(false);
    if (!settingsPaths.has(path)) {
      setSettingsOpen(false);
    }
    navigate(path);
  };

  const logout = async () => {
    try {
      await financeApi.logout();
    } finally {
      localStorage.removeItem("financeToken");
      window.location.href = "/";
    }
  };

  return (
    <>
      {isMobile && mobileOpen ? (
        <div
          className="mobile-sidebar-backdrop"
          aria-hidden="true"
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setMobileOpen(false);
          }}
        />
      ) : null}
      <aside className={`sidebar${mobileOpen ? " menu-open" : ""}`}>
      <div className="brand">
        <div className="brand-identity">
          <span className={`brand-avatar${user.avatar_data_url ? " has-image" : ""}`} style={{ backgroundColor: user.avatar_data_url ? undefined : userAvatarColor(user) }}>
            {user.avatar_data_url ? <img src={user.avatar_data_url} alt="" /> : userInitials(user)}
          </span>
          <div>
            <strong>Aibros Finance</strong>
            <span>{user.display_name} · {group.name}</span>
          </div>
        </div>
        <button
          className="mobile-menu-toggle"
          type="button"
          aria-label="Toggle navigation menu"
          aria-expanded={mobileOpen}
          aria-controls="mobileMenuPanel"
          onClick={() => setMobileOpen((current) => !current)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
      {shouldRenderMenuPanel ? (
        <div className="mobile-menu-panel" id="mobileMenuPanel">
          <nav className="nav" aria-label="Main navigation">
            <Button className={location.pathname === "/home" ? "active" : ""} type="button" onClick={() => goTo("/home")}>
              <span className="nav-icon"></span>Home
            </Button>
            <Button className={location.pathname === "/records" ? "active" : ""} type="button" onClick={() => goTo("/records")}>
              <span className="nav-icon"></span>Records
            </Button>
            <Button className={location.pathname === "/statistics" ? "active" : ""} type="button" onClick={() => goTo("/statistics")}>
              <span className="nav-icon"></span>Statistics
            </Button>
            <Button className={location.pathname === "/recurring-payments" ? "active" : ""} type="button" onClick={() => goTo("/recurring-payments")}>
              <span className="nav-icon"></span>Recurring payments
            </Button>
            <Button
              className={`settings-toggle${isSettingsPage ? " active" : ""}`}
              type="button"
              aria-expanded={effectiveSettingsOpen}
              onClick={() => setSettingsOpen((current) => !current)}
            >
              <span className="nav-icon"></span>Settings <span className="chevron">{effectiveSettingsOpen ? "⌃" : "⌄"}</span>
            </Button>
            <div className={`settings-subnav${effectiveSettingsOpen ? " open" : ""}`}>
              <Button className={location.pathname === "/settings" ? "active" : ""} type="button" onClick={() => goTo("/settings")}>
                <span className="subnav-dot"></span>Overview
              </Button>
              <Button className={location.pathname === "/settings/users-family" ? "active" : ""} type="button" onClick={() => goTo("/settings/users-family")}>
                <span className="subnav-dot"></span>Users and group
              </Button>
              <Button className={location.pathname === "/settings/bank-accounts" ? "active" : ""} type="button" onClick={() => goTo("/settings/bank-accounts")}>
                <span className="subnav-dot"></span>Bank accounts
              </Button>
              <Button className={location.pathname === "/settings/currencies" ? "active" : ""} type="button" onClick={() => goTo("/settings/currencies")}>
                <span className="subnav-dot"></span>Currencies
              </Button>
              <Button className={location.pathname === "/settings/categories" ? "active" : ""} type="button" onClick={() => goTo("/settings/categories")}>
                <span className="subnav-dot"></span>Categories
              </Button>
              <Button className={location.pathname === "/settings/tags" ? "active" : ""} type="button" onClick={() => goTo("/settings/tags")}>
                <span className="subnav-dot"></span>Tags
              </Button>
              <Button className={location.pathname === "/settings/linked-transactions" ? "active" : ""} type="button" onClick={() => goTo("/settings/linked-transactions")}>
                <span className="subnav-dot"></span>Linked transactions
              </Button>
              <Button className={location.pathname === "/settings/mcp" ? "active" : ""} type="button" onClick={() => goTo("/settings/mcp")}>
                <span className="subnav-dot"></span>MCP access (Beta)
              </Button>
              <Button className={location.pathname === "/settings/feedback" ? "active" : ""} type="button" onClick={() => goTo("/settings/feedback")}>
                <span className="subnav-dot"></span>Feedback
              </Button>
              {debugEnabled ? (
                <Button className={location.pathname === "/settings/debug" ? "active" : ""} type="button" onClick={() => goTo("/settings/debug")}>
                  <span className="subnav-dot"></span>Debug
                </Button>
              ) : null}
            </div>
          </nav>
          <div className="sidebar-footer">
            <TextField
              className="sidebar-group-select"
              select
              label="Group"
              value={selectedGroupId}
              size="small"
              slotProps={{ select: { native: true } }}
              onChange={(event) => onSelectGroup(event.target.value)}
            >
              {groups.map((group) => (
                <option key={group.id} value={group.id}>{group.name}{group.is_blocked ? " (blocked)" : ""}</option>
              ))}
            </TextField>
            <Button type="button" onClick={logout}>Logout</Button>
            <span className="app-version">{__APP_VERSION__}</span>
          </div>
        </div>
      ) : null}
      </aside>
    </>
  );
}
